/**
 * Typed client for the Bot Partner admin endpoints in
 * /integrations-admin/discord/* (nurev-api plan PR 5) — backs the Bot
 * Partners screen. A partnership is a Discord server connected to a
 * bot_partner app. Every endpoint answers in the { success, data }
 * envelope, so each call goes through unwrap(); a refusal comes back as
 * { success: false, err: { message: CODE } } (see apiErrorCode).
 */
import { client, unwrap } from './client';

export type PartnershipStatus =
  'active' | 'disconnected' | 'lapsed' | 'paused' | 'error' | 'pending_setup';

export const PARTNERSHIP_STATUSES: PartnershipStatus[] = [
  'active',
  'disconnected',
  'lapsed',
  'paused',
  'error',
  'pending_setup'
];

export type EndedReason =
  'removed' | 'disconnected' | 'account_closed' | 'reconciled';

export interface PartnerTerms {
  activeSharePct: number;
  originalReferrerPct: number;
  pilotPartner: boolean;
  grandfatheredAt: string | null;
}

export interface PartnershipLapse {
  at: string;
  finishedAt: string | null;
  // The lapse ended the app's partnership (its last live server): the app
  // lost its terms. Its installs keep their lifetime share (2026-11-04).
  appEnded: boolean;
  // Installs whose lifetime share the lapse ended: only lapses applied
  // before 2026-11-04 ended any, so 0 from then on.
  devices: number;
  rehomed: number;
  restoredAt: string | null;
}

export interface Partnership {
  _id: string;
  guildId: string;
  name?: string;
  status: PartnershipStatus;
  connectedAt: string | null;
  disconnectedAt: string | null;
  windowEndsAt: string | null;
  lapsedAt: string | null;
  endedReason: EndedReason | null;
  lastLapse: PartnershipLapse | null;
  canRestore: boolean;
  app: {
    _id: string;
    name: string;
    currencyName: string;
    terms: PartnerTerms;
  } | null;
  publisher: { _id: string; email?: string; name?: string };
}

export interface ProductServerConfig {
  configured: boolean;
  appId: string | null;
  guildId: string | null;
}

export interface PartnershipListParams {
  status?: PartnershipStatus | '';
  q?: string;
  page?: number;
  limit?: number;
}

export interface PartnershipListResponse {
  docs: Partnership[];
  total: number;
  page: number;
  limit: number;
  productServer: ProductServerConfig;
}

export interface RestoreResult {
  integrationId: string;
  status: PartnershipStatus;
  windowEndsAt: string | null;
  devicesRestored: number;
  termsRestored: boolean;
  membersRestored: number;
}

export interface PilotResult {
  integrationId: string;
  appId: string;
  terms: PartnerTerms;
}

export interface Absorption {
  _id: string;
  publisherId: string;
  amountUsd: number;
  reason: 'partner_dormancy';
  masterId: string;
  clockStartedAt: string;
  lastLapsedAt: string;
  dormancyDays: number;
  runId: string | null;
  reversedAt: string | null;
  reversedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AbsorptionListParams {
  publisherId?: string;
  limit?: number;
}

export interface AbsorptionListResponse {
  docs: Absorption[];
  // PARTNER_DORMANCY_ENABLED: without it the rule only reports.
  armed: boolean;
  // The payout rail is up; the rule never absorbs while it's down.
  railReady: boolean;
}

export interface ReverseResult {
  absorptionId: string;
  amountUsd: number;
  reversedAt: string;
}

const BASE = '/integrations-admin/discord';

export const fetchPartnerships = (params: PartnershipListParams) =>
  unwrap<PartnershipListResponse>(
    client.get(`${BASE}/partnerships`, {
      params: {
        status: params.status || undefined,
        q: params.q?.trim() || undefined,
        page: params.page,
        limit: params.limit
      }
    })
  );

export const restorePartnership = (integrationId: string, note?: string) =>
  unwrap<RestoreResult>(
    client.post(`${BASE}/partnerships/${integrationId}/restore`, {
      note: note?.trim() || undefined
    })
  );

export const setPilotPartner = (
  integrationId: string,
  pilotPartner: boolean,
  note?: string
) =>
  unwrap<PilotResult>(
    client.patch(`${BASE}/partnerships/${integrationId}`, {
      pilotPartner,
      note: note?.trim() || undefined
    })
  );

export const fetchAbsorptions = (params: AbsorptionListParams) =>
  unwrap<AbsorptionListResponse>(
    client.get(`${BASE}/absorptions`, {
      params: {
        publisherId: params.publisherId?.trim() || undefined,
        limit: params.limit
      }
    })
  );

export const reverseAbsorption = (id: string, note?: string) =>
  unwrap<ReverseResult>(
    client.post(`${BASE}/absorptions/${id}/reverse`, {
      note: note?.trim() || undefined
    })
  );

/** The API's refusal code ({ success: false, err: { message } }), if any. */
export const apiErrorCode = (e: unknown): string | null => {
  const message = (
    e as { response?: { data?: { err?: { message?: unknown } } } }
  )?.response?.data?.err?.message;
  return typeof message === 'string' && message ? message : null;
};
